// src/pages/Schedule.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./Schedule.css";
import DoctorBadge from "../components/DoctorBadge";
import { supabase } from "../lib/supabase";

// === 時段常數 ===
const SESSIONS = [
  { key: "morning", label: "早診", time: "08:30–12:00" },
  { key: "afternoon", label: "午診", time: "14:30–17:30" },
  { key: "evening", label: "晚診", time: "18:30–21:30" },
];

function getWeekDates(baseDate = new Date()) {
  const d = new Date(baseDate);
  const day = d.getDay(); // 0=Sun, 1=Mon...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return Array.from({ length: 7 }, (_, i) => {
    const cur = new Date(d);
    cur.setDate(d.getDate() + i);
    return cur;
  });
}
function toDateKey(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function Schedule() {
  const [baseDate, setBaseDate] = useState(new Date());
  const [rvOpen, setRvOpen] = useState(false);
  const [rvSlot, setRvSlot] = useState(null);
  const [lookupOpen, setLookupOpen] = useState(false);

  const weekDates = useMemo(() => getWeekDates(baseDate), [baseDate]);
  const weekdayMap = ["日", "一", "二", "三", "四", "五", "六"];

  // rosterMap: Map<dateKey, { morning:[], afternoon:[], evening:[] }>
  const [rosterMap, setRosterMap] = useState(new Map());
  const [loadingRoster, setLoadingRoster] = useState(false);

  // 讀取該週 roster
  useEffect(() => {
    let abort = false;
    async function fetchRoster() {
      setLoadingRoster(true);
      const from = toDateKey(weekDates[0]);
      const to = toDateKey(weekDates[6]);
      const { data, error } = await supabase
        .from("roster")
        .select("*")
        .gte("date", from)
        .lte("date", to)
        .order("date", { ascending: true });
      if (!abort) {
        if (error) {
          console.error("[Schedule] roster fetch error:", error);
          setRosterMap(new Map());
        } else {
          const m = new Map();
          (data || []).forEach((r) =>
            m.set(r.date, {
              morning: Array.isArray(r.morning) ? r.morning : [],
              afternoon: Array.isArray(r.afternoon) ? r.afternoon : [],
              evening: Array.isArray(r.evening) ? r.evening : [],
            })
          );
          setRosterMap(m);
        }
        setLoadingRoster(false);
      }
    }
    fetchRoster();
    return () => {
      abort = true;
    };
  }, [weekDates]);

  return (
    <section className="schedule-page">
      <div className="schedule-inner">
        <h2 className="schedule-title">本週門診時刻表</h2>

        {/* 週切換 */}
        <div className="week-controls">
          <button
            className="btn"
            onClick={() =>
              setBaseDate(
                (prev) =>
                  new Date(
                    prev.getFullYear(),
                    prev.getMonth(),
                    prev.getDate() - 7
                  )
              )
            }
          >
            ⬅ 上一週
          </button>
          <button className="btn" onClick={() => setBaseDate(new Date())}>
            回到本週
          </button>
          <button
            className="btn"
            onClick={() =>
              setBaseDate(
                (prev) =>
                  new Date(
                    prev.getFullYear(),
                    prev.getMonth(),
                    prev.getDate() + 7
                  )
              )
            }
          >
            下一週 ➡
          </button>
        </div>

        {/* 表格 */}
        <div className="schedule-table-wrap">
          <table className="schedule-table">
            <thead>
              <tr>
                <th className="session-col">時段</th>
                {weekDates.map((d) => (
                  <th key={d.toISOString()}>
                    <div className="day-head">
                      <div>週{weekdayMap[d.getDay()]}</div>
                      <div className="date-sub">{`${
                        d.getMonth() + 1
                      }/${d.getDate()}`}</div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {SESSIONS.map((s) => (
                <tr key={s.key}>
                  <td className="session-col">
                    <div className="session-name">{s.label}</div>
                    <div className="session-time">{s.time}</div>
                  </td>

                  {weekDates.map((d) => {
                    const dateKey = toDateKey(d);
                    const day = rosterMap.get(dateKey);

                    // ✅ 沒有這天的 roster 記錄 → 視為「尚未開放預約」
                    if (!day) {
                      return (
                        <td key={dateKey + s.key}>
                          <div className="doctor-cell notopen">
                            <span className="notopen-text">尚未開放預約</span>
                          </div>
                        </td>
                      );
                    }

                    const doctors = (day[s.key] || []).filter(Boolean);
                    const isClosed =
                      doctors.length === 0 ||
                      doctors.every((n) => !n || n === "—" || n === "-");

                    return (
                      <td key={dateKey + s.key}>
                        <div
                          className={`doctor-cell ${isClosed ? "closed" : ""}`}
                        >
                          {isClosed ? (
                            <span className="closed-text">休診</span>
                          ) : (
                            <>
                              {doctors.slice(0, 2).map((name, idx) => (
                                <DoctorBadge
                                  key={`${dateKey}-${s.key}-${idx}-${name}`}
                                  doctorName={name}
                                  dateKey={dateKey}
                                  sessionKey={s.key}
                                  onReserve={({
                                    doctorName,
                                    dateKey,
                                    sessionKey,
                                  }) => {
                                    setRvSlot({
                                      doctorName,
                                      dateKey,
                                      sessionKey,
                                    });
                                    setRvOpen(true);
                                  }}
                                />
                              ))}
                              {doctors.length > 2 && (
                                <span className="doctor-more">
                                  +{doctors.length - 2}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 預約查詢 / 取消 → 改成 LINE 連結 */}
        <div className="lookup-bar">
          <a
            href="https://lin.ee/bBukgUN"
            className="lookup-btn"
            target="_blank"
            rel="noopener noreferrer"
            title="前往 LINE 官方帳號預約"
          >
            Line線上預約看診
          </a>
        </div>

        <p className="schedule-note">
          實際看診醫師可能會有變動，可主動聯繫 03-4571515 確認看診醫師
        </p>
      </div>

      {/* 掛號 Modal */}
    </section>
  );
}
